import { TypeInitProps, TypeRequest } from '../../types';
import { encryptParams } from '../../utils/index';
import { WEB_URL } from '../../utils/urls';
export default class RebindAPI {
  private request: any;
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request } = props;
    this.request = request;
  }
  /**
   * @param {string} mobile - the mobile about to unbind
   * @param {boolean} use_bind_mobile - true for using the binded mobile, false for using the mobile in the parameters
   * @return {Promise} send result
   */
  sendCodeToOldMobile = (params: {
    mobile?: string;
    use_bind_mobile?: boolean;
    extra_params?: object;
  }) => {
    const { mobile, use_bind_mobile = true, extra_params = {} } = params;
    const type = use_bind_mobile ? 22 : 28;
    const data = encryptParams({ mobile, type, ...extra_params }, [
      'mobile',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} code - the code received by the old mobile
   * @param {boolean} use_bind_mobile - true for using the binded mobile, false for using the mobile in the parameters
   * @return {Promise} verify result
   */
  validateOldMobile = (params: {
    code: string;
    use_bind_mobile?: boolean;
    extra_params?: object;
  }) => {
    const { code, use_bind_mobile = true, extra_params = {} } = params;
    const type = use_bind_mobile ? 22 : 28;
    const data = encryptParams({ code, type, ...extra_params }, [
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.VERIFY_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} mobile - the mobile about to bind
   * @param {string} old_mobile - the mobile about to unbind
   * @param {string} ticket - the ticket returned from <validateOldMobile>
   * @return {Promise} send result
   */
  sendCodeToNewMobile = (params: {
    mobile: string;
    old_mobile?: string;
    ticket: string;
    extra_params?: object;
  }) => {
    const { mobile, old_mobile, ticket, extra_params = {} } = params;
    const data = encryptParams(
      { mobile, old_mobile, type: 20, ticket, ...extra_params },
      ['mobile', 'old_mobile', 'type']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_MOBILE_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} mobile - the mobile about to bind
   * @param {string} code - the captcha
   * @param {string} ticket - the ticket returned from <validateOldMobile>
   * @param {number} kick_other - whether kick off the login status of other devices, 0 for no, 1 for yes
   * @return {Promise} bind result
   */
  rebindMobile = (params: {
    mobile: string;
    code: string;
    ticket?: string;
    kick_other?: number;
    extra_params?: object;
  }) => {
    const { mobile, code, ticket, kick_other = 0, extra_params = {} } = params;
    const data = encryptParams(
      { mobile, code, ticket, kick_other, ...extra_params },
      ['mobile', 'code']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.MOBILE_REBIND,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - the email about to unbind
   * @return {Promise} send result
   */
  sendCodeToOldEmail = (
    params: { email?: string; extra_params?: object } = {}
  ) => {
    const { email, extra_params = {} } = params;
    const data = encryptParams({ email, type: 6, ...extra_params }, [
      'email',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_EMAIL_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - the email about to unbind
   * @param {string} code - the code received by the old mobile
   * @return {Promise} verify result
   */
  validateOldEmail = (params: {
    email?: string;
    code: string;
    extra_params?: object;
  }) => {
    const { email, code, extra_params = {} } = params;
    const data = encryptParams({ email, code, type: 6, ...extra_params }, [
      'email',
      'code',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.VERIFY_EMAIL_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - the email about to bind
   * @param {string} ticket - the ticket
   * @return {Promise} send result
   */
  sendCodeToNewEmail = (params: {
    email: string;
    ticket: string;
    extra_params?: object;
  }) => {
    const { email, ticket, extra_params = {} } = params;
    const data = encryptParams({ email, ticket, type: 7, ...extra_params }, [
      'email',
      'type',
    ]);
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.SEND_EMAIL_CODE,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };

  /**
   * @param {string} email - the email about to bind
   * @param {string} code - the captcha
   * @param {string} ticket - the ticket returned from <validateOldEmail>
   * @return {Promise} bind result
   */
  rebindEmail = (params: {
    email: string;
    code: string;
    ticket: string;
    extra_params?: object;
  }) => {
    const { email, code, ticket, extra_params = {} } = params;
    const data = encryptParams(
      { email, code, type: 7, ticket, ...extra_params },
      ['email', 'code', 'type']
    );
    return new Promise((resolve, reject) => {
      this.request({
        url: WEB_URL.EMAIL_REBIND,
        method: 'POST',
        data,
      })
        .then((data: any) => {
          resolve(data);
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  };
}
